'use strict';

const utils = require('public/js/helpers/utils');

/**
 * Posts landing acquisition params after FPC HIT (PHP SessionSource skipped).
 * Exposes a page-load gate so ATC cannot overwrite the session before attribution settles.
 */

const ATTRIBUTION_KEYS = [
	'origin',
	'gclid',
	'gbraid',
	'wbraid',
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'promocode',
];
/** Exempt from ajax deferral; must match GATE_SETTLING_ENDPOINTS. */
const ATTRIBUTION_PATH = '/ajax/session/attribution';
const ATTRIBUTION_READY_PROMISE_KEY = '__rp_session_attribution_ready_promise';
/** Backstop for callers sequenced behind the gate; matches the supplier gate bound. */
const ATTRIBUTION_READY_TIMEOUT_MS = 20000;

let pendingParams = null;
let resolveAttributionReady = null;

/**
 * @param {URLSearchParams} searchParams
 * @returns {Record<string, string>|null}
 */
function collectParams(searchParams) {
	/** @type {Record<string, string>} */
	const params = {};
	for (const key of ATTRIBUTION_KEYS) {
		const value = searchParams.get(key);
		if (value !== null && value !== '') {
			params[key] = value;
		}
	}

	const hasAttribution = params.origin !== undefined
		|| params.gclid !== undefined
		|| params.gbraid !== undefined
		|| params.wbraid !== undefined
		|| params.promocode !== undefined
		|| (params.utm_source !== undefined && params.utm_medium !== undefined);

	return hasAttribution ? params : null;
}

/**
 * @returns {Record<string, string>|null}
 */
function readLandingParams() {
	if (typeof globalThis.URLSearchParams !== 'function' || typeof globalThis.location?.search !== 'string') {
		return null;
	}

	return collectParams(new globalThis.URLSearchParams(globalThis.location.search));
}

/**
 * Call before initCsrfBootstrap so the deferral wrapper sees the gate.
 *
 * @returns {void}
 */
function openAttributionGate() {
	if (globalThis[ATTRIBUTION_READY_PROMISE_KEY]) {
		return;
	}

	pendingParams = readLandingParams();
	if (pendingParams === null) {
		return;
	}

	globalThis[ATTRIBUTION_READY_PROMISE_KEY] = new Promise((resolve) => {
		resolveAttributionReady = resolve;
	});
}

/**
 * @returns {void}
 */
function markAttributionReady() {
	if (resolveAttributionReady === null) {
		return;
	}

	resolveAttributionReady();
	resolveAttributionReady = null;
}

/**
 * Resolves once the attribution post settled, immediately when no gate was opened.
 *
 * @returns {Promise<void>}
 */
function waitForAttribution() {
	const gate = globalThis[ATTRIBUTION_READY_PROMISE_KEY];
	if (!gate || typeof gate.then !== 'function') {
		return Promise.resolve();
	}

	let releaseTimeoutId = null;
	const releaseOnTimeout = new Promise((resolve) => {
		releaseTimeoutId = setTimeout(() => {
			// Settles the gate itself so the backstop fires once per page, not per caller.
			markAttributionReady();
			resolve();
		}, ATTRIBUTION_READY_TIMEOUT_MS);
	});

	return Promise.race([gate, releaseOnTimeout]).then(() => {
		clearTimeout(releaseTimeoutId);
	});
}

/**
 * @param {Record<string, string>} params
 * @returns {void}
 */
function post(params) {
	const ajax = globalThis.$ajax || globalThis.jQuery?.ajax || globalThis.rp$?.ajax;
	if (typeof ajax !== 'function') {
		markAttributionReady();
		return;
	}

	const request = ajax({
		type: 'POST',
		url: ATTRIBUTION_PATH,
		data: params,
		dataType: 'json',
	});

	if (request && typeof request.always === 'function') {
		request.always(markAttributionReady);
		return;
	}

	markAttributionReady();
}

/**
 * Waits for CSRF bootstrap to settle (shared session), then posts (CSRF-exempt).
 *
 * @param {Promise<unknown>|null|undefined} csrfReady
 * @returns {void}
 */
function initSessionAttribution(csrfReady) {
	if (utils.isBot()) {
		markAttributionReady();
		return;
	}

	const params = pendingParams === null ? readLandingParams() : pendingParams;
	if (params === null) {
		markAttributionReady();
		return;
	}

	const runPost = () => {
		try {
			post(params);
		} catch {
			markAttributionReady();
		}
	};

	if (typeof csrfReady?.then === 'function') {
		// finally runs even when csrfReady rejects; post() releases the gate via ajax.always.
		void Promise.resolve(csrfReady).finally(runPost).catch(() => {});
		return;
	}

	runPost();
}

module.exports = {
	ATTRIBUTION_KEYS,
	ATTRIBUTION_PATH,
	ATTRIBUTION_READY_PROMISE_KEY,
	collectParams,
	initSessionAttribution,
	markAttributionReady,
	openAttributionGate,
	waitForAttribution,
};
